import { Box, Tooltip, alpha, useTheme } from '@mui/material';
import { LuSun, LuMoon, LuMonitor } from 'react-icons/lu';
import AppIcon, { type AppIconType } from '../../icons/app-icon';
import {
  useThemeMode,
  ThemePreference,
} from '../../../context/theme-mode-context';

const OPTIONS: { value: ThemePreference; label: string; icon: AppIconType }[] = [
  { value: 'light', label: 'Light', icon: LuSun },
  { value: 'system', label: 'Auto', icon: LuMonitor },
  { value: 'dark', label: 'Dark', icon: LuMoon },
];

interface ThemeModeToggleProps {
  /** Show text labels next to the icons (e.g. in settings). Icons-only otherwise. */
  showLabels?: boolean;
  size?: 'small' | 'medium';
}

/**
 * A compact light / auto / dark segmented control. Reads and writes the theme
 * preference, so the navbar and the profile stay in sync automatically.
 */
const ThemeModeToggle = ({
  showLabels = false,
  size = 'medium',
}: ThemeModeToggleProps) => {
  const theme = useTheme();
  const { preference, setPreference } = useThemeMode();
  const dim = size === 'small' ? 30 : 34;
  const iconSize = size === 'small' ? 16 : 18;

  return (
    <Box
      role='radiogroup'
      aria-label='Theme'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        p: '3px',
        borderRadius: 999,
        backgroundColor: alpha(theme.palette.text.primary, 0.06),
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {OPTIONS.map((opt) => {
        const active = preference === opt.value;
        const node = (
          <Box
            key={opt.value}
            role='radio'
            aria-checked={active}
            aria-label={opt.label}
            tabIndex={0}
            onClick={() => setPreference(opt.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setPreference(opt.value);
              }
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: showLabels ? 0.75 : 0,
              height: dim,
              minWidth: showLabels ? 'auto' : dim,
              px: showLabels ? 1.5 : 0,
              borderRadius: 999,
              cursor: 'pointer',
              userSelect: 'none',
              fontSize: '0.82rem',
              fontWeight: 700,
              color: active ? 'primary.main' : 'text.secondary',
              backgroundColor: active ? 'background.paper' : 'transparent',
              boxShadow: active ? theme.shadows[1] : 'none',
              transition: 'color 0.15s ease, background-color 0.15s ease',
              '&:hover': { color: active ? 'primary.main' : 'text.primary' },
            }}
          >
            <AppIcon
              icon={opt.icon}
              size={iconSize}
              color={active ? 'primary.main' : 'inherit'}
            />
            {showLabels && <span>{opt.label}</span>}
          </Box>
        );

        return showLabels ? (
          node
        ) : (
          <Tooltip key={opt.value} title={opt.label} arrow>
            {node}
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default ThemeModeToggle;
